import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
// import { useParams } from "react-router-dom";
import { Trophy, Users, Target, Calendar, Loader2 } from "lucide-react";
import { AffiliateHeaderContext } from "../../context/AffiliateHeaderContext";
import { formatCurrency } from "../../utils/helpers";
// import { calculateTieredCommission } from "../../utils/commissionTiers";
import useAffiliateData from "../../hooks/useAffiliateData";
import Card from "../ui/Card";
import Button from "../ui/Button";
import CustomSelect from "../ui/CustomSelect";

const LeaderboardPage = () => {
  // const { affiliateName } = useParams(); // route param unused
  const setHeaderContent = useContext(AffiliateHeaderContext);

  // Use proper hooks for getting all affiliates data
  const { getAllAffiliateData } = useAffiliateData();

  const [activeTab, setActiveTab] = useState("affiliates");
  const [isLoading, setIsLoading] = useState(true);
  const [topAffiliates, setTopAffiliates] = useState([]);
  const [topAds, setTopAds] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState("");
  const [error, setError] = useState(null);

  // Date filtering state
  const [dateFilterType, setDateFilterType] = useState("week");
  const [singleDate, setSingleDate] = useState("");
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");

  // Set header content
  useEffect(() => {
    setHeaderContent(
      <div className="flex items-center space-x-4">
        <div className="w-2 h-8 bg-gradient-to-b from-[#B8460E] to-[#8B3A0E] rounded-full"></div>
        <div>
          <h1 className="text-2xl font-bold text-white">Leaderboard</h1>
          <p className="text-gray-400 text-sm">
            Top performing affiliates and ads
          </p>
        </div>
      </div>
    );

    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  // Calculate current week dates (Mon-Sun) - robust for Sundays
  const getCurrentWeekDates = () => {
    const now = new Date();
    const monday = new Date(now);
    const day = now.getDay(); // 0 = Sun, 1 = Mon, ...
    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // if Sunday, go back 6 days
    monday.setDate(diff);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const formatDate = (date) => date.toISOString().split("T")[0];

    return {
      start: formatDate(monday),
      end: formatDate(sunday),
      display: `${monday.toLocaleDateString()} - ${sunday.toLocaleDateString()}`,
    };
  };

  // Calculate last week's dates (Mon-Sun)
  const getLastWeekDates = useCallback(() => {
    const currentWeek = getCurrentWeekDates();
    const start = new Date(currentWeek.start);
    const end = new Date(currentWeek.end);
    start.setDate(start.getDate() - 7);
    end.setDate(end.getDate() - 7);
    const formatDate = (date) => date.toISOString().split("T")[0];
    return {
      start: formatDate(start),
      end: formatDate(end),
      display: `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
    };
  }, []);

  // Calculate current month dates
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatDate = (date) => date.toISOString().split("T")[0];

    return {
      start: formatDate(firstDay),
      end: formatDate(lastDay),
      display: `${firstDay.toLocaleDateString("en-US", { month: "long", year: "numeric" })}`,
    };
  };

  // Calculate current quarter dates
  const getCurrentQuarterDates = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Determine quarter start month (0, 3, 6, 9)
    const quarterStartMonth = Math.floor(currentMonth / 3) * 3;

    const firstDay = new Date(currentYear, quarterStartMonth, 1);
    const lastDay = new Date(currentYear, quarterStartMonth + 3, 0);

    const formatDate = (date) => date.toISOString().split("T")[0];

    const quarterNumber = Math.floor(currentMonth / 3) + 1;

    return {
      start: formatDate(firstDay),
      end: formatDate(lastDay),
      display: `Q${quarterNumber} ${currentYear}`,
    };
  };

  // Check if today is the first day of the current week (Monday) and we're viewing current week
  const isFirstDayOfCurrentWeek = useCallback(() => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const isMonday = dayOfWeek === 1;
    const isViewingCurrentWeek = dateFilterType === "week";

    return isMonday && isViewingCurrentWeek;
  }, [dateFilterType]);

  // Get date range based on filter type
  const getDateRange = useMemo(() => {
    if (dateFilterType === "week") {
      // On Mondays, show last week's fully available data
      return isFirstDayOfCurrentWeek()
        ? getLastWeekDates()
        : getCurrentWeekDates();
    } else if (dateFilterType === "month") {
      return getCurrentMonthDates();
    } else if (dateFilterType === "quarter") {
      return getCurrentQuarterDates();
    } else if (dateFilterType === "single") {
      return {
        start: singleDate,
        end: singleDate,
        display: new Date(singleDate).toLocaleDateString(),
      };
    } else if (dateFilterType === "range") {
      return {
        start: dateRangeStart,
        end: dateRangeEnd,
        display: `${new Date(dateRangeStart).toLocaleDateString()} - ${new Date(dateRangeEnd).toLocaleDateString()}`,
      };
    }
    return getCurrentWeekDates();
  }, [
    dateFilterType,
    singleDate,
    dateRangeStart,
    dateRangeEnd,
    isFirstDayOfCurrentWeek,
    getLastWeekDates,
  ]);

  // Load leaderboard data using optimized methods
  const loadLeaderboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log("🚀 Loading leaderboard data...");

      // Prepare date filter for API
      let dateFilter = null;
      if (dateFilterType === "week") {
        // Use smart week: last week on Mondays, otherwise current week
        const weekDates = isFirstDayOfCurrentWeek()
          ? getLastWeekDates()
          : getCurrentWeekDates();
        dateFilter = {
          type: "range",
          dateRangeStart: weekDates.start,
          dateRangeEnd: weekDates.end,
        };
      } else if (dateFilterType === "month") {
        // For current month
        const monthDates = getCurrentMonthDates();
        dateFilter = {
          type: "range",
          dateRangeStart: monthDates.start,
          dateRangeEnd: monthDates.end,
        };
      } else if (dateFilterType === "quarter") {
        // For current quarter
        const quarterDates = getCurrentQuarterDates();
        dateFilter = {
          type: "range",
          dateRangeStart: quarterDates.start,
          dateRangeEnd: quarterDates.end,
        };
      } else if (dateFilterType === "single" && singleDate) {
        dateFilter = {
          type: "single",
          singleDate: singleDate,
        };
      } else if (dateFilterType === "range" && dateRangeStart && dateRangeEnd) {
        dateFilter = {
          type: "range",
          dateRangeStart: dateRangeStart,
          dateRangeEnd: dateRangeEnd,
        };
      }

      // Get all affiliates data using proper service
      const result = await getAllAffiliateData("all", dateFilter);

      if (!result || !result.affiliates) {
        throw new Error("No data returned from service");
      }

      console.log(`✅ Loaded data for ${result.affiliates.length} affiliates`);

      // Process affiliate leaderboard - sort by commission and take top 10
      const sortedAffiliates = result.affiliates
        .filter((affiliate) => affiliate.totalCommission > 0) // Only show affiliates with earnings
        .sort((a, b) => b.totalCommission - a.totalCommission)
        .slice(0, 10);

      setTopAffiliates(sortedAffiliates);

      // Top ads feature is coming soon - no need to load ads data
      setTopAds([]);

      // Set current period display
      const periodDisplay = getDateRange.display;
      setCurrentPeriod(periodDisplay);

      console.log(
        `✅ Leaderboard data processed - ${sortedAffiliates.length} affiliates loaded`
      );
    } catch (err) {
      console.error("❌ Error loading leaderboard data:", err);
      setError(`Failed to load leaderboard data: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [
    dateFilterType,
    singleDate,
    dateRangeStart,
    dateRangeEnd,
    isFirstDayOfCurrentWeek,
    getAllAffiliateData,
    getDateRange.display,
    getLastWeekDates,
  ]);

  // Load data on mount and when filters change
  useEffect(() => {
    loadLeaderboardData();
  }, [
    loadLeaderboardData,
    dateFilterType,
    singleDate,
    dateRangeStart,
    dateRangeEnd,
  ]);

  // Initialize dates on mount
  useEffect(() => {
    const weekDates = getCurrentWeekDates();
    setSingleDate(weekDates.start);
    setDateRangeStart(weekDates.start);
    setDateRangeEnd(weekDates.end);
  }, []);

  const renderRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <span className="text-2xl">🥇</span>;
      case 2:
        return <span className="text-2xl">🥈</span>;
      case 3:
        return <span className="text-2xl">🥉</span>;
      default:
        return <span className="text-lg text-gray-400">#{rank}</span>;
    }
  };

  const renderAffiliateLeaderboard = () => (
    <div className="space-y-4">
      {topAffiliates.map((affiliate, index) => (
        <Card
          key={affiliate.name}
          className="bg-white/5 backdrop-blur-xl border-white/10 hover:bg-white/10 transition-all duration-300"
        >
          <div className="flex items-center justify-between p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">{renderRankIcon(index + 1)}</div>
              <div>
                <h3 className="text-xl font-bold text-white">
                  {affiliate.name}
                </h3>
                <p className="text-gray-400 text-sm">
                  {affiliate.adCount} ads •{" "}
                  {formatCurrency(affiliate.totalAdSpend)} spend
                </p>
                <p className="text-gray-300 text-xs">
                  Avg ROAS: {(affiliate.avgROAS || 0).toFixed(2)}x • Revenue:{" "}
                  {formatCurrency(affiliate.totalRevenue)}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-[#B8460E]">
                {formatCurrency(affiliate.totalCommission)}
              </div>
              <p className="text-gray-400 text-sm">Commission Earned</p>
            </div>
          </div>
        </Card>
      ))}

      {topAffiliates.length === 0 && !isLoading && (
        <div className="text-center py-16">
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 max-w-md mx-auto">
            <Trophy className="h-16 w-16 mx-auto text-gray-400 mb-6" />
            <div className="space-y-4">
              <>
                <h3 className="text-2xl font-bold text-white">
                  No Data Available
                </h3>
                <p className="text-gray-300 text-lg font-medium">
                  📊 No Performance Data
                </p>
                <p className="text-gray-400 text-base">
                  No affiliate data available for this selected time period. Try
                  adjusting your date range.
                </p>
              </>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAdLeaderboard = () => (
    <div className="space-y-4">
      <div className="text-center py-16">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 max-w-md mx-auto">
          <Target className="h-16 w-16 mx-auto text-gray-400 mb-6" />
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-white">Coming Soon</h3>
            <p className="text-gray-300 text-lg font-medium">
              🚧 Feature in Development
            </p>
            <p className="text-gray-400 text-base">
              We're working hard to bring you detailed ad performance
              leaderboards. This feature will be available soon!
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="bg-red-900/20 border-red-500/30 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">!</span>
            </div>
            <div>
              <h3 className="text-white font-semibold">
                Error Loading Leaderboard
              </h3>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          </div>
          <div className="mt-4">
            <Button
              onClick={loadLeaderboardData}
              className="bg-white/10 hover:bg-white/20 text-white"
            >
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Date Filter Controls */}
      <Card className="bg-white/5 backdrop-blur-xl border-white/10">
        <div className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-[#B8460E]" />
              <h3 className="text-lg font-semibold text-white">Time Period</h3>
            </div>

            <div className="flex items-center space-x-4">
              <CustomSelect
                value={dateFilterType}
                onChange={setDateFilterType}
                options={[
                  { value: "week", label: "This Week" },
                  { value: "month", label: "This Month" },
                  { value: "quarter", label: "This Quarter" },
                  { value: "single", label: "Single Date" },
                  { value: "range", label: "Date Range" },
                ]}
              />

              {dateFilterType === "single" && (
                <input
                  type="date"
                  value={singleDate}
                  onChange={(e) => setSingleDate(e.target.value)}
                  className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                />
              )}

              {dateFilterType === "range" && (
                <div className="flex items-center space-x-2">
                  <input
                    type="date"
                    value={dateRangeStart}
                    onChange={(e) => setDateRangeStart(e.target.value)}
                    className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="date"
                    value={dateRangeEnd}
                    onChange={(e) => setDateRangeEnd(e.target.value)}
                    className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                  />
                </div>
              )}
            </div>
          </div>

          {currentPeriod && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-gray-300 text-center">
                <strong>Selected Period:</strong> {currentPeriod}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-white/5 backdrop-blur-xl rounded-xl p-1 border border-white/10">
        <button
          onClick={() => setActiveTab("affiliates")}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 flex-1 justify-center ${
            activeTab === "affiliates"
              ? "bg-[#B8460E] text-white shadow-lg"
              : "text-gray-300 hover:text-white hover:bg-white/10"
          }`}
        >
          <Users className="h-4 w-4" />
          Top Affiliates
        </button>
        <button
          onClick={() => setActiveTab("ads")}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 flex-1 justify-center ${
            activeTab === "ads"
              ? "bg-[#B8460E] text-white shadow-lg"
              : "text-gray-300 hover:text-white hover:bg-white/10"
          }`}
        >
          <Target className="h-4 w-4" />
          Top Ads
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-16">
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-[#B8460E] mx-auto mb-4" />
            <p className="text-white text-lg">Loading leaderboard data...</p>
            <p className="text-gray-400 text-sm mt-2">This may take a moment</p>
          </div>
        </div>
      )}

      {/* Content */}
      {!isLoading && (
        <div className="space-y-6">
          {activeTab === "affiliates" && renderAffiliateLeaderboard()}
          {activeTab === "ads" && renderAdLeaderboard()}
        </div>
      )}

      {/* Summary Stats */}
      {!isLoading &&
        !error &&
        (topAffiliates.length > 0 || topAds.length > 0) && (
          <Card className="bg-white/5 backdrop-blur-xl border-white/10">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#B8460E]">
                    {topAffiliates.length}
                  </div>
                  <p className="text-gray-400 text-sm">Active Affiliates</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#B8460E]">
                    {topAds.length}
                  </div>
                  <p className="text-gray-400 text-sm">Top Performing Ads</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#B8460E]">
                    {formatCurrency(
                      topAffiliates.reduce(
                        (sum, a) => sum + (a.totalCommission || 0),
                        0
                      )
                    )}
                  </div>
                  <p className="text-gray-400 text-sm">Total Commission</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#B8460E]">
                    {formatCurrency(
                      topAffiliates.reduce(
                        (sum, a) => sum + (a.totalRevenue || 0),
                        0
                      )
                    )}
                  </div>
                  <p className="text-gray-400 text-sm">Total Revenue</p>
                </div>
              </div>
            </div>
          </Card>
        )}
    </div>
  );
};

export default LeaderboardPage;
